import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Row } from '@vendasta/galaxy/table';
import { CrmFieldService, StandardExternalIds } from '@galaxy/crm/services/field-service';
import { ActivityAssociationInterface } from '@vendasta/crm';
import { ContactProvider } from '@galaxy/email-ui/shared';
import { EmailProvider } from './send-email/constants';
import { CrmSendEmailComponent } from './send-email/send-email.component';

export interface ProviderConnectionOption {
  connectionId: string;
  label: string;
  isDelegated: boolean;
}

export interface EmailComposerData {
  namespace: string;
  userId: string;
  contactIds: string[];
  emails: string[];
  provider: EmailProvider;
  connections: ProviderConnectionOption[];
  contactProvider?: ContactProvider;
  isReply?: boolean;
  replySubject?: string;
  replyBody?: string;
  replyVisibility?: string;
  providerMessageThreadId?: string;
  associations?: ActivityAssociationInterface[];
  inReplyToMessageId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class EmailWorkflowService {
  private dialog = inject(MatDialog);
  private crmFieldService = inject(CrmFieldService);
  private breakpointObserver = inject(BreakpointObserver);

  openEmailComposer(
    namespace: string,
    userId: string,
    contactIds: string[],
    emails: string[] = [],
    provider: EmailProvider,
    connections: ProviderConnectionOption[] = [],
    contactProvider?: ContactProvider,
  ): void {
    this._openDialog({ namespace, userId, contactIds, emails, provider, connections, contactProvider });
  }

  openEmailComposerFromRows(
    namespace: string,
    userId: string,
    rows: Row[],
    provider: EmailProvider,
    connections: ProviderConnectionOption[] = [],
    contactProvider?: ContactProvider,
  ): void {
    const contactIds = rows.map((row) => row.id);
    const emailFieldId = this.crmFieldService.getFieldId(StandardExternalIds.Email);
    const emails = rows.map((row) => {
      const emailField = row.data[emailFieldId];
      return emailField?.value || '';
    });
    this.openEmailComposer(namespace, userId, contactIds, emails, provider, connections, contactProvider);
  }

  openEmailReply(
    namespace: string,
    userId: string,
    contactIds: string[],
    emails: string[],
    provider: EmailProvider,
    replySubject: string,
    replyBody?: string,
    replyVisibility?: string,
    providerMessageThreadId?: string,
    associations?: ActivityAssociationInterface[],
    connections: ProviderConnectionOption[] = [],
    contactProvider?: ContactProvider,
    inReplyToMessageId?: string,
  ): void {
    this._openDialog({
      namespace,
      userId,
      contactIds,
      emails,
      provider,
      connections,
      contactProvider,
      isReply: true,
      replySubject,
      replyBody,
      replyVisibility,
      providerMessageThreadId,
      associations,
      inReplyToMessageId,
    });
  }

  private _openDialog(data: EmailComposerData): void {
    const isMobile = this.breakpointObserver.isMatched('(max-width: 599px)');
    this.dialog.open(CrmSendEmailComponent, {
      data,
      width: isMobile ? '100vw' : '1000px',
      height: isMobile ? '100vh' : '95vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      autoFocus: false,
      panelClass: 'email-composer-dialog',
    });
  }
}
